import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getPhotos, getCategories, getInquiries, getAnalyticsSummary } from '../config/api';

export default function Dashboard() {
  const [stats,     setStats]     = useState({ photos: 0, categories: 0, inquiries: 0, unread: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [photos, cats, inqs] = await Promise.all([
          getPhotos(),
          getCategories(),
          getInquiries(),
        ]);
        setStats({
          photos:     photos.data?.data?.length     ?? 0,
          categories: cats.data?.data?.length       ?? 0,
          inquiries:  inqs.data?.meta?.total        || 0,
          unread:     inqs.data?.meta?.unread       || 0,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    getAnalyticsSummary(30)
      .then(res => setAnalytics(res.data?.data ?? null))
      .catch(() => {});
  }, []);

  const cardStyle = {
    background: 'var(--color-surface)',
    padding:    20,
  };

  const statLabel = {
    fontSize:      11,
    color:         'var(--color-muted)',
    fontFamily:    'var(--font-body)',
    marginBottom:  8,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  };

  const statValue = {
    fontSize:   28,
    fontWeight: 500,
    color:      'var(--color-text)',
    fontFamily: 'var(--font-body)',
  };

  const sectionLabel = {
    fontFamily:   'var(--font-body)',
    fontSize:     13,
    fontWeight:   500,
    color:        'var(--color-text)',
    marginBottom: 14,
  };

  return (
    <AdminLayout title="Dashboard">

      {loading ? (
        <div style={{ color: 'var(--color-muted)', fontFamily: 'var(--font-body)', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          <div className="stat-cards-scroll" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 32 }}>
            <Link to="/admin/photo-gallery" style={{ textDecoration: 'none', display: 'block' }}>
              <div className="stat-card-link" style={cardStyle}>
                <div style={statLabel}>Total Photos</div>
                <div style={statValue}>{stats.photos}</div>
              </div>
            </Link>
            <Link to="/admin/categories" style={{ textDecoration: 'none', display: 'block' }}>
              <div className="stat-card-link" style={cardStyle}>
                <div style={statLabel}>Categories</div>
                <div style={statValue}>{stats.categories}</div>
              </div>
            </Link>
            <Link to="/admin/inquiries" style={{ textDecoration: 'none', display: 'block' }}>
              <div className="stat-card-link" style={cardStyle}>
                <div style={statLabel}>Inquiries</div>
                <div style={statValue}>{stats.inquiries}</div>
                {stats.unread > 0 && (
                  <span style={{ display: 'inline-block', background: '#FEF3C7', color: '#92400E', fontSize: 10, padding: '2px 8px', borderRadius: 10, marginTop: 6, fontFamily: 'var(--font-body)' }}>
                    {stats.unread} unread
                  </span>
                )}
              </div>
            </Link>
          </div>

          <div style={{ ...sectionLabel, marginBottom: 14 }}>
            Quick actions
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 32 }}>
            {[
              ['+ Upload photo',  '/admin/photos'     ],
              ['+ Add category',  '/admin/categories' ],
              ['View inquiries',  '/admin/inquiries'  ],
              ['Edit about page', '/admin/about'      ],
            ].map(([label, path]) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                style={{ padding: '10px 18px', fontSize: 12, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer', borderRadius: 6, fontFamily: 'var(--font-body)', transition: 'border-color 0.2s' }}
              >
                {label}
              </button>
            ))}
          </div>

          {analytics && (
            <>
              <div style={sectionLabel}>
                Analytics — last 30 days
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
                <div style={cardStyle}>
                  <div style={{ ...statLabel, marginBottom: 12 }}>Daily visits</div>
                  {analytics.daily.length === 0 ? (
                    <div style={{ color: 'var(--color-muted)', fontFamily: 'var(--font-body)', fontSize: 12 }}>No data yet</div>
                  ) : (() => {
                    const maxSessions = Math.max(...analytics.daily.map(d => d.sessions), 1);
                    return (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 64 }}>
                        {analytics.daily.map(({ date, sessions }) => (
                          <div
                            key={date}
                            title={`${date}: ${sessions}`}
                            style={{
                              flex:         1,
                              background:   sessions === maxSessions ? '#D4A843' : 'var(--color-border)',
                              borderRadius: '2px 2px 0 0',
                              height:       `${(sessions / maxSessions) * 100}%`,
                              minHeight:    sessions > 0 ? 2 : 0,
                            }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div style={cardStyle}>
                  <div style={{ ...statLabel, marginBottom: 12 }}>By page</div>
                  {analytics.byPage.map(({ page, sessions }) => (
                    <div
                      key={page}
                      style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-body)', fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--color-border)' }}
                    >
                      <span style={{ color: 'var(--color-muted)' }}>{page}</span>
                      <span style={{ color: 'var(--color-text)' }}>{sessions}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

    </AdminLayout>
  );
}
